import { apiClient } from '@/shared/services/client'
import type {
  PaginatedSorteos,
  Sorteo,
  SorteoCreatePayload,
  SorteoUpdatePayload,
  FiltrosSorteos,
  ParticipantesListResponse,
  BulkUploadResult,
  GanadoresListResponse,
  GanadorResult,
  GanadorCreatePayload,
  ResetearResult,
  LaboratorioSimple,
  Premio,
  PremioActivoResponse,
  PremioCreatePayload,
  PremioUpdatePayload,
} from '../types/sorteos.types'

const BASE = '/sorteos'

// === LABORATORIOS ===

export async function getLaboratoriosSorteos(): Promise<LaboratorioSimple[]> {
  const { data } = await apiClient.get(`${BASE}/laboratorios/`)
  return data
}

// === SORTEOS ===

export async function getSorteos(params?: FiltrosSorteos): Promise<PaginatedSorteos> {
  const { data } = await apiClient.get(`${BASE}/`, { params })
  return data
}

export async function getSorteo(id: string): Promise<Sorteo> {
  const { data } = await apiClient.get(`${BASE}/${id}/`)
  return data
}

export async function crearSorteo(payload: SorteoCreatePayload): Promise<Sorteo> {
  const { data } = await apiClient.post(`${BASE}/`, payload)
  return data
}

export async function actualizarSorteo(id: string, payload: SorteoUpdatePayload): Promise<Sorteo> {
  const { data } = await apiClient.put(`${BASE}/${id}/`, payload)
  return data
}

export async function eliminarSorteo(id: string): Promise<{ message: string }> {
  const { data } = await apiClient.delete(`${BASE}/${id}/`)
  return data
}

// === PARTICIPANTES ===

export async function getParticipantes(sorteoId: string): Promise<ParticipantesListResponse> {
  const { data } = await apiClient.get(`${BASE}/${sorteoId}/participantes/`)
  return data
}

export async function crearParticipante(sorteoId: string, payload: { nombre: string; correo?: string; pais?: string }): Promise<unknown> {
  const { data } = await apiClient.post(`${BASE}/${sorteoId}/participantes/`, payload)
  return data
}

export async function cargarExcelParticipantes(sorteoId: string, archivo: File): Promise<BulkUploadResult> {
  const formData = new FormData()
  formData.append('archivo', archivo)
  const { data } = await apiClient.post(`${BASE}/${sorteoId}/participantes/cargar-excel/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function eliminarParticipante(sorteoId: string, participanteId: number): Promise<{ success: boolean; mensaje: string }> {
  const { data } = await apiClient.delete(`${BASE}/${sorteoId}/participantes/${participanteId}/`)
  return data
}

export async function eliminarParticipantes(sorteoId: string): Promise<{ success: boolean; mensaje: string }> {
  const { data } = await apiClient.delete(`${BASE}/${sorteoId}/participantes/`)
  return data
}

// === GANADORES ===

export async function getGanadores(sorteoId: string): Promise<GanadoresListResponse> {
  const { data } = await apiClient.get(`${BASE}/${sorteoId}/ganadores/`)
  return data
}

export async function registrarGanador(sorteoId: string, payload: GanadorCreatePayload): Promise<GanadorResult> {
  const { data } = await apiClient.post(`${BASE}/${sorteoId}/ganadores/`, payload)
  return data
}

export async function crearYGirar(sorteoId: string, payload: { nombre: string; opcion_index: number }): Promise<GanadorResult> {
  const { data } = await apiClient.post(`${BASE}/${sorteoId}/ganadores/crear-y-girar/`, payload)
  return data
}

export async function resetearGanadores(sorteoId: string, pais?: string): Promise<ResetearResult> {
  const { data } = await apiClient.post(`${BASE}/${sorteoId}/ganadores/resetear/`, null, {
    params: pais ? { pais } : undefined,
  })
  return data
}

export async function eliminarGanador(sorteoId: string, ganadorId: number): Promise<{ success: boolean; mensaje: string }> {
  const { data } = await apiClient.delete(`${BASE}/${sorteoId}/ganadores/${ganadorId}/`)
  return data
}

export async function deshacerUltimoGanador(sorteoId: string): Promise<ResetearResult> {
  const { data } = await apiClient.post(`${BASE}/${sorteoId}/ganadores/deshacer-ultimo/`)
  return data
}

export async function descargarGanadores(sorteoId: string): Promise<Blob> {
  const { data } = await apiClient.get(`${BASE}/${sorteoId}/ganadores/descargar/`, {
    responseType: 'blob',
  })
  return data
}

// === PREMIOS ===

export async function getPremios(sorteoId: string): Promise<Premio[]> {
  const { data } = await apiClient.get(`${BASE}/${sorteoId}/premios/`)
  return data
}

export async function getPremioActivo(sorteoId: string): Promise<PremioActivoResponse> {
  const { data } = await apiClient.get(`${BASE}/${sorteoId}/premios/activo/`)
  return data
}

export async function crearPremio(sorteoId: string, payload: PremioCreatePayload): Promise<Premio> {
  const { data } = await apiClient.post(`${BASE}/${sorteoId}/premios/`, payload)
  return data
}

export async function actualizarPremio(sorteoId: string, premioId: string, payload: PremioUpdatePayload): Promise<Premio> {
  const { data } = await apiClient.put(`${BASE}/${sorteoId}/premios/${premioId}/`, payload)
  return data
}

export async function eliminarPremio(sorteoId: string, premioId: string): Promise<{ success: boolean; mensaje: string }> {
  const { data } = await apiClient.delete(`${BASE}/${sorteoId}/premios/${premioId}/`)
  return data
}
